import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Shield, Crown, User, ChevronRight, ChevronLeft, X, Sparkles } from 'lucide-react';
import { 
  COGNITIVE_LANES, 
  COGNITIVE_LEVELS, 
  LEVEL_QUESTIONS,
  getProfileDescription,
  calculateLevelFromAnswers,
  calculateCognitiveSignature,
  INDUSTRIES
} from './onboardingData';
import {
  OrganizationSetup,
  TeamBuilder,
  ProjectSeeder,
  CognitiveIntelligenceReport
} from './OnboardingSteps';

// ================================================
// ONBOARDING CONTEXT
// ================================================
const OnboardingContext = createContext(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

// ================================================
// NEURAL PARTICLE BACKGROUND (Reused from Landing)
// ================================================
const NODE_COLORS = ['#00E5A0', '#E8D5A3', '#FF6B6B', '#9B7FE8', '#4A8C6A'];

const NeuralBackground = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const nodesRef = useRef([]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Initialize nodes
    const nodeCount = 100;
    nodesRef.current = Array.from({ length: nodeCount }, () => {
      const sizeRoll = Math.random();
      let radius;
      if (sizeRoll < 0.5) radius = 0.5 + Math.random() * 0.5;
      else if (sizeRoll < 0.85) radius = 1.5 + Math.random() * 1.5;
      else radius = 3 + Math.random() * 2;
      
      const speedMultiplier = 0.2 + Math.random() * 0.8;
      
      return {
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        angle: Math.random() * Math.PI * 2,
        angularVel: (Math.random() - 0.5) * 0.002,
        speed: (0.1 + Math.random() * 0.15) * speedMultiplier,
        radius,
        baseRadius: radius,
        color: NODE_COLORS[Math.floor(Math.random() * NODE_COLORS.length)],
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.01 + Math.random() * 0.02,
        depth: speedMultiplier
      };
    });
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const nodes = nodesRef.current;
      
      // Draw connection lines
      ctx.lineWidth = 0.5;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 100) {
            const opacity = (1 - dist / 100) * 0.06;
            ctx.strokeStyle = `rgba(0, 229, 160, ${opacity})`;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      
      // Update and draw nodes
      nodes.forEach(node => {
        node.angle += node.angularVel;
        node.x += Math.cos(node.angle) * node.speed;
        node.y += Math.sin(node.angle) * node.speed;
        node.pulse += node.pulseSpeed;
        
        if (Math.random() < 0.001) {
          node.angularVel = (Math.random() - 0.5) * 0.003;
        }
        
        if (node.x < -20) node.x = canvas.width + 20;
        if (node.x > canvas.width + 20) node.x = -20;
        if (node.y < -20) node.y = canvas.height + 20;
        if (node.y > canvas.height + 20) node.y = -20;
        
        const pulseScale = 1 + Math.sin(node.pulse) * 0.2;
        const currentRadius = node.baseRadius * pulseScale;
        
        if (node.baseRadius > 2) {
          const gradient = ctx.createRadialGradient(
            node.x, node.y, 0,
            node.x, node.y, currentRadius * 4
          );
          gradient.addColorStop(0, node.color + '40');
          gradient.addColorStop(1, node.color + '00');
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(node.x, node.y, currentRadius * 4, 0, Math.PI * 2);
          ctx.fill();
        }
        
        const depthOpacity = 0.4 + node.depth * 0.6;
        ctx.globalAlpha = depthOpacity;
        ctx.fillStyle = node.color;
        ctx.beginPath();
        ctx.arc(node.x, node.y, currentRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="fixed inset-0 pointer-events-none"
      style={{ opacity: 0.6 }}
    />
  );
};

// ================================================
// PROGRESS BAR
// ================================================
const ProgressBar = ({ currentStep, totalSteps, laneColor }) => {
  const progress = (currentStep / totalSteps) * 100;
  
  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <div className="h-1 bg-[#1A2E1F]">
        <div 
          className="h-full transition-all duration-500 ease-out"
          style={{ 
            width: `${progress}%`,
            backgroundColor: laneColor || '#00E5A0'
          }}
        />
      </div>
      <div className="absolute top-3 left-1/2 -translate-x-1/2">
        <span className="text-xs text-[#6B8C78]">
          Step {currentStep} of {totalSteps}
        </span>
      </div>
    </div>
  );
};

// ================================================
// PAGE TRANSITION WRAPPER
// ================================================
const PageTransition = ({ children, direction = 'forward' }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, [children]);
  
  return (
    <div 
      className={`transition-all duration-500 ${
        isVisible 
          ? 'opacity-100 translate-x-0' 
          : direction === 'forward' 
            ? 'opacity-0 translate-x-8' 
            : 'opacity-0 -translate-x-8'
      }`}
    >
      {children}
    </div>
  );
};

// ================================================
// MAIN ONBOARDING OVERLAY
// ================================================
const OnboardingOverlay = ({ onComplete }) => {
  const { demoLogin, authMode, userProfile } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [track, setTrack] = useState(null); // 'executive' | 'team_member'
  const [currentStep, setCurrentStep] = useState(0); // 0 = welcome screen
  const [direction, setDirection] = useState('forward');
  
  // Profile state
  const [primaryLane, setPrimaryLane] = useState(null);
  const [supportingLanes, setSupportingLanes] = useState([]);
  const [levelAnswers, setLevelAnswers] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [calculatedLevel, setCalculatedLevel] = useState(null);
  const [showProcessing, setShowProcessing] = useState(false);
  
  // Org setup state (executive track)
  const [orgName, setOrgName] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [industry, setIndustry] = useState('');
  const [structureType, setStructureType] = useState('');
  const [businessUnits, setBusinessUnits] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  
  // Calculate cognitive signature
  const cognitiveSignature = primaryLane && calculatedLevel
    ? calculateCognitiveSignature(primaryLane, supportingLanes, calculatedLevel)
    : null;
  
  // Get lane color for theming
  const laneColor = primaryLane ? COGNITIVE_LANES[primaryLane]?.color : null;
  
  // Total steps based on track
  const totalSteps = track === 'executive' ? 7 : 3;
  
  // Navigation
  const goNext = () => {
    setDirection('forward');
    setCurrentStep(prev => prev + 1);
  };
  
  const goBack = () => {
    setDirection('backward');
    setCurrentStep(prev => prev - 1);
  };
  
  // Handle track selection
  const handleTrackSelect = (selectedTrack) => {
    setTrack(selectedTrack);
    setCurrentStep(1);
  };
  
  // Handle lane selection
  const handleLaneSelect = (laneId) => {
    if (primaryLane === laneId) {
      setPrimaryLane(null);
    } else {
      setPrimaryLane(laneId);
      // Remove from supporting if was selected there
      setSupportingLanes(prev => prev.filter(l => l !== laneId));
    }
  };
  
  // Handle supporting lane selection
  const handleSupportingLaneToggle = (laneId) => {
    if (laneId === primaryLane) return;
    
    setSupportingLanes(prev => {
      if (prev.includes(laneId)) {
        return prev.filter(l => l !== laneId);
      } else if (prev.length < 3) {
        return [...prev, laneId];
      }
      return prev;
    });
  };
  
  // Handle level assessment answer
  const handleLevelAnswer = (answerId) => {
    const newAnswers = [...levelAnswers];
    newAnswers[currentQuestionIndex] = answerId;
    setLevelAnswers(newAnswers);
    
    if (currentQuestionIndex < LEVEL_QUESTIONS.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, 300);
    } else {
      // All questions answered - calculate level
      setShowProcessing(true);
      setTimeout(() => {
        const level = calculateLevelFromAnswers(newAnswers);
        setCalculatedLevel(level);
        setShowProcessing(false);
        goNext();
      }, 2000);
    }
  };
  
  // Handle completion
  const handleComplete = async () => {
    // Set skip flag so onboarding doesn't show again in this session
    localStorage.setItem('skip_onboarding_test', 'true');
    
    // If user is already authenticated via Supabase, don't override with demo mode
    if (authMode === 'supabase') {
      onComplete?.({
        track,
        primaryLane,
        supportingLanes,
        level: calculatedLevel,
        cognitiveSignature,
        organization: track === 'executive' ? {
          name: orgName,
          roleTitle,
          industry,
          structureType,
          businessUnits
        } : null,
        teamMembers: track === 'executive' ? teamMembers : null,
        projects: track === 'executive' ? projects : null
      });
      navigate('/pulse');
      return;
    }
    
    // Otherwise, fall back to demo mode
    const result = await demoLogin();
    if (result.success) {
      onComplete?.({
        track,
        primaryLane,
        supportingLanes,
        level: calculatedLevel,
        cognitiveSignature,
        organization: track === 'executive' ? {
          name: orgName,
          roleTitle,
          industry,
          structureType,
          businessUnits
        } : null,
        teamMembers: track === 'executive' ? teamMembers : null,
        projects: track === 'executive' ? projects : null
      });
      navigate('/pulse');
    }
  };
  
  // Render welcome screen (step 0)
  const renderWelcomeScreen = () => (
    <PageTransition direction={direction}>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12">
        {/* Logo */}
        <div className="mb-8 relative">
          <div 
            className="w-20 h-20 rounded-2xl flex items-center justify-center"
            style={{ 
              background: 'linear-gradient(135deg, #00E5A0 0%, #00D090 100%)',
              boxShadow: '0 0 60px rgba(0, 229, 160, 0.3)'
            }}
          >
            <Sparkles className="w-10 h-10 text-[#080F0D]" />
          </div>
        </div>
        
        {/* Headlines */}
        <h1 className="text-3xl md:text-4xl font-bold text-white text-center mb-4 max-w-2xl">
          Before we build your workspace, let's understand how you think.
        </h1>
        <p className="text-lg text-[#6B8C78] text-center max-w-xl mb-12">
          CognitiveOS is the first platform built around human cognition. Your setup experience begins with you — not your tasks.
        </p>
        
        {/* Track Selection Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl w-full">
          {/* Executive Track */}
          <button
            onClick={() => handleTrackSelect('executive')}
            className="group relative p-6 rounded-2xl text-left transition-all duration-300 hover:scale-[1.02]"
            style={{ 
              backgroundColor: '#0D1810',
              borderTop: '3px solid #00E5A0'
            }}
            data-testid="track-executive"
          >
            <div className="absolute inset-0 rounded-2xl border border-[#1A2E1F] group-hover:border-[#00E5A0]/30 transition-colors" />
            <div className="relative">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(0, 229, 160, 0.1)' }}
              >
                <Crown className="w-6 h-6 text-[#00E5A0]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                I'm setting up for my team.
              </h3>
              <p className="text-[#6B8C78] text-sm mb-4">
                Directors, executives, and founders building a workspace for others.
              </p>
              <div className="flex items-center gap-2 text-[#00E5A0] font-medium">
                Set Up My Organization
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>
          
          {/* Team Member Track */}
          <button
            onClick={() => handleTrackSelect('team_member')}
            className="group relative p-6 rounded-2xl text-left transition-all duration-300 hover:scale-[1.02]"
            style={{ 
              backgroundColor: '#0D1810',
              borderTop: '3px solid #00E5A0'
            }}
            data-testid="track-team-member"
          >
            <div className="absolute inset-0 rounded-2xl border border-[#1A2E1F] group-hover:border-[#00E5A0]/30 transition-colors" />
            <div className="relative">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(0, 229, 160, 0.1)' }}
              >
                <User className="w-6 h-6 text-[#00E5A0]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                I was invited to join a workspace.
              </h3>
              <p className="text-[#6B8C78] text-sm mb-4">
                Team members joining an existing organization.
              </p>
              <div className="flex items-center gap-2 text-[#00E5A0] font-medium">
                Join My Team
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>
        </div>
      </div>
    </PageTransition>
  );
  
  // Render Lane Discovery (Executive Step 1)
  const renderLaneDiscovery = () => (
    <PageTransition direction={direction}>
      <div className="min-h-screen px-6 py-16 overflow-y-auto">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <span 
              className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4"
              style={{ backgroundColor: 'rgba(0, 229, 160, 0.1)', color: '#00E5A0' }}
            >
              STEP 1 OF {totalSteps} — YOUR COGNITIVE PROFILE
            </span>
            <h2 className="text-3xl font-bold text-white mb-4">
              Every great leader operates from a primary cognitive lane. Let's find yours.
            </h2>
            <p className="text-[#6B8C78] max-w-2xl mx-auto">
              These aren't job titles. They're the ten distinct modes of human thinking that every organization operates across. Select the one that describes how you do your most natural, energized work.
            </p>
          </div>
          
          {/* Lane Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {Object.values(COGNITIVE_LANES).map(lane => {
              const isSelected = primaryLane === lane.id;
              const isSupporting = supportingLanes.includes(lane.id);
              
              return (
                <button
                  key={lane.id}
                  onClick={() => handleLaneSelect(lane.id)}
                  className={`relative p-5 rounded-xl text-left transition-all duration-300 ${
                    isSelected ? 'scale-[1.02]' : 'hover:scale-[1.01]'
                  }`}
                  style={{ 
                    backgroundColor: '#0D1810',
                    borderLeft: `4px solid ${lane.color}`,
                    boxShadow: isSelected ? `0 0 30px ${lane.color}30` : 'none'
                  }}
                  data-testid={`lane-${lane.id}`}
                >
                  <div 
                    className="absolute inset-0 rounded-xl transition-opacity duration-300"
                    style={{ 
                      background: `linear-gradient(135deg, ${lane.color}08 0%, transparent 50%)`,
                      opacity: isSelected ? 1 : 0
                    }}
                  />
                  <div 
                    className="absolute inset-0 rounded-xl border transition-colors duration-300"
                    style={{ 
                      borderColor: isSelected ? `${lane.color}60` : '#1A2E1F'
                    }}
                  />
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ 
                          backgroundColor: lane.color,
                          boxShadow: `0 0 8px ${lane.color}80`
                        }}
                      />
                      <h3 className="font-bold text-white">{lane.name}</h3>
                      {isSelected && (
                        <div className="ml-auto w-6 h-6 rounded-full bg-white flex items-center justify-center">
                          <svg className="w-4 h-4 text-[#080F0D]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-[#6B8C78] leading-relaxed">
                      {lane.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Supporting Lanes Selection */}
          {primaryLane && (
            <div className="mt-8 p-6 rounded-xl" style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}>
              <h4 className="text-white font-semibold mb-2">
                Which lanes do you also operate in regularly?
              </h4>
              <p className="text-[#6B8C78] text-sm mb-4">
                Select up to 3 supporting lanes (optional)
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.values(COGNITIVE_LANES)
                  .filter(lane => lane.id !== primaryLane)
                  .map(lane => {
                    const isSelected = supportingLanes.includes(lane.id);
                    return (
                      <button
                        key={lane.id}
                        onClick={() => handleSupportingLaneToggle(lane.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          isSelected ? 'text-white' : 'text-[#6B8C78] hover:text-white'
                        }`}
                        style={{
                          backgroundColor: isSelected ? `${lane.color}20` : 'transparent',
                          border: isSelected ? `2px dashed ${lane.color}60` : '1px solid #1A2E1F'
                        }}
                      >
                        <span className="flex items-center gap-2">
                          <span 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: lane.color }}
                          />
                          {lane.name}
                          {isSelected && (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
          
          {/* Next Button */}
          <div className="flex justify-center mt-10">
            <button
              onClick={goNext}
              disabled={!primaryLane}
              className={`px-8 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                primaryLane 
                  ? 'text-[#080F0D] hover:scale-105' 
                  : 'bg-[#1A2E1F] text-[#6B8C78] cursor-not-allowed'
              }`}
              style={primaryLane ? {
                backgroundColor: laneColor || '#00E5A0',
                boxShadow: `0 0 20px ${laneColor || '#00E5A0'}40`
              } : {}}
              data-testid="next-btn"
            >
              Continue
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </PageTransition>
  );
  
  // Render Level Assessment (Executive Step 2)
  const renderLevelAssessment = () => {
    if (showProcessing) {
      return (
        <PageTransition direction={direction}>
          <div className="min-h-screen flex flex-col items-center justify-center px-6">
            <div className="text-center">
              <div className="relative w-16 h-16 mx-auto mb-6">
                <div 
                  className="absolute inset-0 rounded-full animate-ping"
                  style={{ backgroundColor: `${laneColor}30` }}
                />
                <div 
                  className="relative w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${laneColor}20` }}
                >
                  <div 
                    className="w-4 h-4 rounded-full animate-pulse"
                    style={{ backgroundColor: laneColor }}
                  />
                </div>
              </div>
              <p className="text-xl text-white font-medium">
                Analyzing your cognitive engagement pattern...
              </p>
            </div>
          </div>
        </PageTransition>
      );
    }
    
    const currentQuestion = LEVEL_QUESTIONS[currentQuestionIndex];
    
    return (
      <PageTransition direction={direction}>
        <div className="min-h-screen px-6 py-16">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <span 
                className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4"
                style={{ backgroundColor: `${laneColor}15`, color: laneColor }}
              >
                STEP 2 OF {totalSteps} — YOUR ENGAGEMENT LEVEL
              </span>
              <h2 className="text-3xl font-bold text-white mb-4">
                How do you engage with your work? Answer honestly — there's no wrong answer.
              </h2>
              <p className="text-[#6B8C78]">
                These four questions reveal how you naturally show up in your organization.
              </p>
            </div>
            
            {/* Question Progress */}
            <div className="flex gap-2 mb-8 justify-center">
              {LEVEL_QUESTIONS.map((_, idx) => (
                <div 
                  key={idx}
                  className={`w-12 h-1.5 rounded-full transition-colors ${
                    idx < currentQuestionIndex ? 'bg-white' 
                    : idx === currentQuestionIndex ? '' 
                    : 'bg-[#1A2E1F]'
                  }`}
                  style={idx === currentQuestionIndex ? { backgroundColor: laneColor } : {}}
                />
              ))}
            </div>
            
            {/* Question */}
            <div className="text-center mb-8">
              <span className="text-[#6B8C78] text-sm">Question {currentQuestionIndex + 1} of 4</span>
              <h3 className="text-xl text-white font-medium mt-2">
                {currentQuestion.question}
              </h3>
            </div>
            
            {/* Options */}
            <div className="space-y-3">
              {currentQuestion.options.map((option, idx) => {
                const isSelected = levelAnswers[currentQuestionIndex] === option.id;
                return (
                  <button
                    key={option.id}
                    onClick={() => handleLevelAnswer(option.id)}
                    className={`w-full p-5 rounded-xl text-left transition-all duration-300 ${
                      isSelected ? 'scale-[1.02]' : 'hover:scale-[1.01]'
                    }`}
                    style={{ 
                      backgroundColor: '#0D1810',
                      border: isSelected ? `2px solid ${laneColor}` : '1px solid #1A2E1F',
                      boxShadow: isSelected ? `0 0 20px ${laneColor}30` : 'none'
                    }}
                    data-testid={`level-option-${option.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          isSelected ? 'border-transparent' : 'border-[#1A2E1F]'
                        }`}
                        style={isSelected ? { backgroundColor: laneColor } : {}}
                      >
                        {isSelected && (
                          <svg className="w-4 h-4 text-[#080F0D]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="text-white">{option.text}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Back Button */}
            {currentQuestionIndex === 0 && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={goBack}
                  className="px-6 py-2 text-[#6B8C78] hover:text-white transition-colors flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back to Lane Selection
                </button>
              </div>
            )}
          </div>
        </div>
      </PageTransition>
    );
  };
  
  // Render Profile Reveal (Executive Step 3)
  const renderProfileReveal = () => {
    if (!primaryLane || !calculatedLevel) return null;
    
    const lane = COGNITIVE_LANES[primaryLane];
    const level = COGNITIVE_LEVELS[calculatedLevel];
    const description = getProfileDescription(primaryLane, calculatedLevel);
    
    return (
      <PageTransition direction={direction}>
        <div className="min-h-screen flex">
          {/* Left Panel - Lane Color */}
          <div 
            className="hidden lg:flex w-2/5 flex-col items-center justify-center p-12"
            style={{ 
              background: `linear-gradient(135deg, ${lane.color}40 0%, ${lane.color}10 100%)`
            }}
          >
            <div 
              className="w-6 h-6 rounded-full mb-6 animate-pulse"
              style={{ 
                backgroundColor: lane.color,
                boxShadow: `0 0 40px ${lane.color}`
              }}
            />
            <h2 
              className="text-4xl font-black writing-mode-vertical text-white/90"
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', letterSpacing: '0.2em' }}
            >
              {lane.name.toUpperCase()}
            </h2>
          </div>
          
          {/* Right Panel - Content */}
          <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
            <div className="max-w-xl">
              {/* Mobile Lane Color Bar */}
              <div 
                className="lg:hidden h-2 w-24 rounded-full mb-6"
                style={{ backgroundColor: lane.color }}
              />
              
              {/* Label */}
              <span 
                className="text-sm font-semibold tracking-wider"
                style={{ color: lane.color }}
              >
                YOUR COGNITIVE PROFILE
              </span>
              
              {/* Headlines */}
              <h1 className="text-4xl font-bold text-white mt-2 mb-2">
                You are a {lane.name} thinker.
              </h1>
              <h2 className="text-xl text-[#6B8C78] mb-8">
                Operating at {level.name}
              </h2>
              
              {/* Description Card */}
              <div 
                className="p-6 rounded-xl mb-8"
                style={{ 
                  backgroundColor: '#0D1810',
                  border: `1px solid ${lane.color}30`
                }}
              >
                <p className="text-[#EDF5F0] leading-relaxed">
                  {description}
                </p>
              </div>
              
              {/* Supporting Lanes */}
              {supportingLanes.length > 0 && (
                <div className="mb-8">
                  <h4 className="text-sm text-[#6B8C78] mb-3">YOUR SUPPORTING LANES</h4>
                  <div className="flex gap-2 flex-wrap">
                    {supportingLanes.map(laneId => {
                      const sl = COGNITIVE_LANES[laneId];
                      return (
                        <span 
                          key={laneId}
                          className="px-3 py-1.5 rounded-full text-sm font-medium text-white"
                          style={{ backgroundColor: `${sl.color}30` }}
                        >
                          <span 
                            className="inline-block w-2 h-2 rounded-full mr-2"
                            style={{ backgroundColor: sl.color }}
                          />
                          {sl.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Cognitive Signature Scores */}
              {cognitiveSignature && (
                <div 
                  className="p-6 rounded-xl mb-8"
                  style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}
                >
                  <h4 className="text-sm text-[#6B8C78] mb-4">COGNITIVE SIGNATURE</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'Strategic Reach', value: cognitiveSignature.strategicReach },
                      { label: 'Execution Orientation', value: cognitiveSignature.executionOrientation },
                      { label: 'Collaborative Capacity', value: cognitiveSignature.collaborativeCapacity },
                      { label: 'Leadership Bandwidth', value: cognitiveSignature.leadershipBandwidth }
                    ].map(score => (
                      <div key={score.label}>
                        <div className="flex justify-between mb-1">
                          <span className="text-xs text-[#6B8C78]">{score.label}</span>
                          <span className="text-xs font-bold text-white">{score.value}</span>
                        </div>
                        <div className="h-2 bg-[#1A2E1F] rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-1000"
                            style={{ 
                              width: `${score.value}%`,
                              backgroundColor: lane.color
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={goNext}
                  className="flex-1 px-6 py-3 rounded-xl font-semibold text-[#080F0D] flex items-center justify-center gap-2 hover:scale-105 transition-transform"
                  style={{ 
                    backgroundColor: lane.color,
                    boxShadow: `0 0 20px ${lane.color}40`
                  }}
                  data-testid="profile-continue-btn"
                >
                  This is me — Continue
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    setCurrentStep(1);
                    setLevelAnswers([]);
                    setCurrentQuestionIndex(0);
                    setCalculatedLevel(null);
                  }}
                  className="px-6 py-3 rounded-xl font-medium text-[#6B8C78] border border-[#1A2E1F] hover:border-[#6B8C78] transition-colors"
                >
                  Adjust my profile
                </button>
              </div>
            </div>
          </div>
        </div>
      </PageTransition>
    );
  };
  
  // Render Organization Setup (Executive Step 4)
  const renderOrganizationSetup = () => (
    <PageTransition direction={direction}>
      <OrganizationSetup
        orgName={orgName}
        setOrgName={setOrgName}
        roleTitle={roleTitle}
        setRoleTitle={setRoleTitle}
        industry={industry}
        setIndustry={setIndustry}
        structureType={structureType}
        setStructureType={setStructureType}
        businessUnits={businessUnits}
        setBusinessUnits={setBusinessUnits}
        laneColor={laneColor || '#00E5A0'}
        onNext={goNext}
        onBack={goBack}
      />
    </PageTransition>
  );
  
  // Render Team Builder (Executive Step 5)
  const renderTeamBuilder = () => (
    <PageTransition direction={direction}>
      <TeamBuilder
        teamMembers={teamMembers}
        setTeamMembers={setTeamMembers}
        laneColor={laneColor || '#00E5A0'}
        onNext={goNext}
        onBack={goBack}
      />
    </PageTransition>
  );
  
  // Render Project Seeder (Executive Step 6)
  const renderProjectSeeder = () => (
    <PageTransition direction={direction}>
      <ProjectSeeder
        projects={projects}
        setProjects={setProjects}
        teamMembers={teamMembers}
        laneColor={laneColor || '#00E5A0'}
        onNext={goNext}
        onBack={goBack}
      />
    </PageTransition>
  );
  
  // Render Intelligence Report (Executive Step 7)
  const renderIntelligenceReport = () => (
    <PageTransition direction={direction}>
      <CognitiveIntelligenceReport
        primaryLane={primaryLane}
        supportingLanes={supportingLanes}
        level={calculatedLevel}
        cognitiveSignature={cognitiveSignature}
        organization={{ name: orgName, roleTitle, industry, structureType, businessUnits }}
        teamMembers={teamMembers}
        laneColor={laneColor || '#00E5A0'}
        onComplete={handleComplete}
        onBack={goBack}
      />
    </PageTransition>
  );
  
  // Main render logic
  const renderCurrentStep = () => {
    if (currentStep === 0) {
      return renderWelcomeScreen();
    }
    
    if (track === 'executive') {
      switch (currentStep) {
        case 1:
          return renderLaneDiscovery();
        case 2:
          return renderLevelAssessment();
        case 3:
          return renderProfileReveal();
        case 4:
          return renderOrganizationSetup();
        case 5:
          return renderTeamBuilder();
        case 6:
          return renderProjectSeeder();
        case 7:
          return renderIntelligenceReport();
        default:
          return renderWelcomeScreen();
      }
    } else {
      // Team member track
      switch (currentStep) {
        case 1:
          return renderLaneDiscovery();
        case 2:
          return renderLevelAssessment();
        case 3:
          return renderProfileReveal();
        default:
          return renderWelcomeScreen();
      }
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 overflow-hidden"
      style={{ backgroundColor: '#080F0D' }}
      data-testid="onboarding-overlay"
    >
      {/* Neural Background */}
      <NeuralBackground />
      
      {/* Skip to Demo Button - Top Right */}
      <button
        onClick={handleComplete}
        data-testid="skip-to-demo-btn"
        className="absolute top-6 right-6 z-50 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 group"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          color: 'rgba(255, 255, 255, 0.7)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 229, 160, 0.15)';
          e.currentTarget.style.borderColor = 'rgba(0, 229, 160, 0.4)';
          e.currentTarget.style.color = '#00E5A0';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
          e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
        }}
      >
        <span>Skip to Demo</span>
        <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      </button>
      
      {/* Progress Bar */}
      {currentStep > 0 && (
        <ProgressBar 
          currentStep={currentStep} 
          totalSteps={totalSteps}
          laneColor={laneColor}
        />
      )}
      
      {/* Content */}
      <div className="relative z-10 h-full overflow-y-auto">
        {renderCurrentStep()}
      </div>
    </div>
  );
};

export default OnboardingOverlay;
